import { motion } from 'framer-motion';
import { useState } from 'react';
import { MapPin, Mail, Clock, Send, Phone, FileText, Loader2, Globe } from 'lucide-react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { toast } from "sonner";
import { GoogleMap } from '../GoogleMap';
import { enquiriesAPI } from '../../services/api';
import { COUNTRIES } from '../shared/countries';
import { EmailVerification } from '../shared/EmailVerification';

export function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    mobile: '',
    company: '',
    country: '',
    subject: '',
    message: ''
  });

  // ⬇️ NEW: loading state and email verification
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEmailVerified, setIsEmailVerified] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isSubmitting) return;

    try {
      const formEl = e.currentTarget as HTMLFormElement;
      const honeypot = (new FormData(formEl).get('website') as string) || '';
      if (honeypot) return;
    } catch { }

    // Check email verification
    if (!isEmailVerified) {
      toast.error('Please verify your email address before submitting');
      return;
    }

    // simple client validation
    if (
      !formData.name.trim() ||
      !formData.email.trim() ||
      !formData.mobile.trim() ||
      !formData.country.trim() ||
      !formData.subject.trim() ||
      !formData.message.trim()
    ) {
      toast.error('Please fill in all required fields');
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error('Please enter a valid email address');
      return;
    }
    const mobileRegex = /^[\+]?[1-9][\d]{0,15}$/;
    const cleanMobile = formData.mobile.replace(/[\s\-\(\)]/g, '');
    if (!mobileRegex.test(cleanMobile) || cleanMobile.length < 7) {
      toast.error('Please enter a valid mobile number');
      return;
    }

    setIsSubmitting(true);
    try {
      // Submit to MongoDB backend API
      await enquiriesAPI.create({
        name: formData.name,
        email: formData.email,
        phone: formData.mobile,
        company: formData.company || 'N/A',
        country: formData.country,
        subject: formData.subject,
        message: formData.message,
      });

      toast.success("Message sent successfully! We'll get back to you within 24 hours.");
      setFormData({ name: '', email: '', mobile: '', company: '', country: '', subject: '', message: '' });
      setIsEmailVerified(false); // Reset email verification
    } catch (err: any) {
      console.error('Error submitting enquiry:', err);
      toast.error(err.message || 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: <MapPin className="w-6 h-6" />,
      title: "amasQIS.ai OMAN",
      details: [
        { text: "Muscat Governorate - Al Mawaleh", type: "address" },
        { text: "P.O. Box: 2223, Postal Code: 123", type: "address" },
        { text: "+968 9130 7385", type: "phone", href: "tel:+96891307385" },
        { text: "View on Maps", type: "link", href: "https://maps.google.com/?q=Muscat+Governorate+Al+Mawaleh+Oman&ll=23.5880,58.3829&z=12" }
      ],
    },
    {
      icon: <MapPin className="w-6 h-6" />,
      title: "amasQIS.ai INDIA",
      details: [
        { text: "Sasurie College Campus, Coimbatore Bypass (NH544) Vijayamangalam, Tirupur Dt, Tamil Nadu – 638056.", type: "address" },
        { text: "+91 90252 26518", type: "phone", href: "tel:+919025226518" },
        { text: "View on Maps", type: "link", href: "https://maps.app.goo.gl/kZnEew7j2UhQPY6Y8" }
      ]
    },
    {
      icon: <MapPin className="w-6 h-6" />,
      title: "amasQIS.ai DUBAI",
      details: [
        { text: "AL SHARIF BUILDING 2,2. post Box :88878, Dubai", type: "address" },
        { text: "+91 77081 13987", type: "phone", href: "tel:+917708113987" },
        { text: "View on Maps", type: "link", href: "https://www.google.com/maps?ll=25.28238,55.347356&z=15&t=m&hl=en-US&gl=US&mapclient=embed&cid=9725375434234313994" }
      ]
    },
    {
      icon: <MapPin className="w-6 h-6" />,
      title: "amasQIS.ai UK",
      details: [
        { text: "128 CITY ROAD, LONDON", type: "address" },
        { text: "UNITED KINGDOM", type: "address" },
        { text: "Postcode: EC1V 2NX", type: "address" },
        { text: "info@amasqis.ai", type: "email", href: "mailto:info@amasqis.ai" },
        { text: "View on Maps", type: "link", href: "https://www.google.com/maps/search/?api=1&query=128%20CITY%20ROAD%2C%20LONDON%2C%20EC1V%202NX%2C%20UNITED%20KINGDOM" }
      ]
    },
    {
      icon: <Mail className="w-6 h-6" />,
      title: "Email Us",
      details: [
        { text: "info@amasqis.ai", type: "email", href: "mailto:info@amasqis.ai" }
      ]
    },
    {
      icon: <Clock className="w-6 h-6" />,
      title: "Business Hours",
      details: [
        { text: "Mon - Fri: 9:00 AM - 6:00 PM", type: "text" },
        { text: "Sat - Sun: Emergency Support", type: "text" }
      ]
    }
  ];

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="py-15 bg-gradient-to-b from-background to-background/50 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20" style={{
          backgroundImage: `url("data:image/svg+xml,${encodeURIComponent('<svg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"><g fill="none" fill-rule="evenodd"><g fill="#00cfff" fill-opacity="0.03"><path d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/></g></g></svg>')}")`
        }} />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="text-5xl md:text-7xl font-bold mb-6 neon-text">
              Get in Touch
            </h1>
            <p className="text-xl md:text-2xl text-muted-foreground text-center mb-8 ">
              Ready to transform your business with AI? Our experts are here to help you
              discover the perfect solution for your unique challenges.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* First Row - Contact Form and Info (2 columns) */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 mb-16 items-stretch">
            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="flex flex-col h-full"
            >
              <Card className="glass p-6 lg:p-8 flex-1 flex flex-col">
                <h2 className="text-2xl lg:text-3xl font-bold mb-4">Send us a Message</h2>
                <p className="text-muted-foreground mb-6 text-justify">
                  Fill out the form below with your details and we'll get back to you within 24 hours.
                  All fields marked with * are required.
                </p>

                <form onSubmit={handleSubmit} className="space-y-4 flex-1 flex flex-col" aria-busy={isSubmitting}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <input type="text" name="website" style={{ display: 'none' }} tabIndex={-1} autoComplete="off" />

                      <Label htmlFor="name">Name *</Label>
                      <Input
                        id="name"
                        type="text"
                        placeholder="Your full name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                        className="glass"
                        disabled={isSubmitting} // ⬅️ disable while sending
                      />
                    </div>
                    <div>
                      <EmailVerification
                        email={formData.email}
                        onEmailChange={(email) => setFormData({ ...formData, email })}
                        onVerificationChange={setIsEmailVerified}
                        disabled={isSubmitting}
                        label="Email"
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="mobile">
                        <Phone className="w-4 h-4 inline mr-2" />
                        Mobile Number *
                      </Label>
                      <Input
                        id="mobile"
                        type="tel"
                        placeholder="Your mobile number"
                        value={formData.mobile}
                        onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
                        required
                        className="glass"
                        disabled={isSubmitting}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="company">Company</Label>
                      <Input
                        id="company"
                        type="text"
                        placeholder="Your company name"
                        value={formData.company}
                        onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                        className="glass"
                        disabled={isSubmitting}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="country">
                      <Globe className="w-4 h-4 inline mr-2" />
                      Country *
                    </Label>
                    <select
                      id="country"
                      value={formData.country}
                      onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                      required
                      className="w-full px-4 py-2 rounded-lg glass border border-input focus:border-primary/50 focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:cursor-not-allowed disabled:opacity-50 [&>option]:bg-background [&>option]:text-foreground"
                      disabled={isSubmitting}
                    >
                      <option value="">Select your country</option>
                      {COUNTRIES.map((country) => (
                        <option key={country} value={country}>
                          {country}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="subject">
                      <FileText className="w-4 h-4 inline mr-2" />
                      Subject *
                    </Label>
                    <Input
                      id="subject"
                      type="text"
                      placeholder="Brief subject of your inquiry"
                      value={formData.subject}
                      onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                      required
                      className="glass"
                      disabled={isSubmitting}
                    />
                  </div>

                  <div className="space-y-2 flex-1 flex flex-col">
                    <Label htmlFor="message">Message *</Label>
                    <Textarea
                      id="message"
                      placeholder="Tell us about your project and how we can help..."
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      required
                      className="glass resize-none flex-1 min-h-[120px]"
                      disabled={isSubmitting}
                    />
                  </div>

                  <Button
                    type="submit"
                    size="lg"
                    className={`w-full rounded-full bg-primary text-primary-foreground hover:neon-glow ${isSubmitting ? 'opacity-60 cursor-not-allowed' : ''}`}
                    disabled={isSubmitting}
                    aria-disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        Sending…
                        <Loader2 className="ml-2 w-5 h-5 animate-spin" />
                      </>
                    ) : (
                      <>
                        Send Message
                        <Send className="ml-2 w-5 h-5" />
                      </>
                    )}
                  </Button>
                </form>
              </Card>
            </motion.div>

            {/* Contact Information */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="flex flex-col h-full"
            >
              <Card className="glass p-6 lg:p-8 flex-1 flex flex-col">
                <div>
                  <h2 className="text-2xl lg:text-3xl font-bold mb-4">Contact Information</h2>
                  <p className="text-muted-foreground mb-6">
                    We're here to help you succeed. Reach out through any of these channels
                    and our team will respond promptly.
                  </p>
                </div>

                <div className="space-y-4 flex-1">
                  {contactInfo.map((info, index) => {
                    // Skip Business Hours as it will be combined with Email Us
                    if (info.title === "Business Hours") return null;
                    
                    // Special handling for Email Us - combine with Business Hours
                    if (info.title === "Email Us") {
                      const businessHours = contactInfo.find(item => item.title === "Business Hours");
                      return (
                        <motion.div
                          key={info.title}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.6, delay: 0.3 + index * 0.1 }}
                          whileHover={{ x: 10 }}
                        >
                          <Card className="glass p-4 hover:border-primary/50 transition-all duration-300">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                              {/* Email Us - Left Column */}
                              <div className="flex items-start space-x-4">
                                <div className="w-10 h-10 bg-gradient-to-br from-primary/20 to-accent/20 rounded-lg flex items-center justify-center flex-shrink-0">
                                  <div className="text-primary">{info.icon}</div>
                                </div>
                                <div className="flex-1 min-w-0">
                                  <h3 className="font-bold mb-1.5">
                                    {info.title}
                                  </h3>
                                  {info.details.map((detail, detailIndex) => (
                                    detail.type === "email" ? (
                                      <a
                                        key={detailIndex}
                                        href={detail.href}
                                        className="text-primary hover:text-accent transition-colors text-sm underline block break-words"
                                      >
                                        {detail.text}
                                      </a>
                                    ) : (
                                      <p key={detailIndex} className="text-muted-foreground text-sm">
                                        {detail.text}
                                      </p>
                                    )
                                  ))}
                                </div>
                              </div>

                              {/* Business Hours - Right Column */}
                              {businessHours && (
                                <div className="flex items-start space-x-4">
                                  <div className="w-10 h-10 bg-gradient-to-br from-primary/20 to-accent/20 rounded-lg flex items-center justify-center flex-shrink-0">
                                    <div className="text-primary">{businessHours.icon}</div>
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <h3 className="font-bold mb-1.5">
                                      {businessHours.title}
                                    </h3>
                                    {businessHours.details.map((detail, detailIndex) => (
                                      <p key={detailIndex} className="text-muted-foreground text-sm">
                                        {detail.text}
                                      </p>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          </Card>
                        </motion.div>
                      );
                    }
                    
                    // Regular rendering for other items (Oman, India, Dubai)
                    return (
                      <motion.div
                        key={info.title}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.3 + index * 0.1 }}
                        whileHover={{ x: 10 }}
                      >
                        <Card className="glass p-4 hover:border-primary/50 transition-all duration-300">
                          <div className="flex items-start space-x-4">
                            <div className="w-10 h-10 bg-gradient-to-br from-primary/20 to-accent/20 rounded-lg flex items-center justify-center flex-shrink-0">
                              <div className="text-primary">{info.icon}</div>
                            </div>
                            <div className="flex-1 min-w-0">
                              <h3 className="font-bold mb-1.5">
                                {info.title}
                              </h3>
                              {info.details.map((detail, detailIndex) => (
                                detail.type === "link" ? (
                                  <a
                                    key={detailIndex}
                                    href={detail.href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-primary hover:text-accent transition-colors text-sm underline block break-words"
                                  >
                                    {detail.text}
                                  </a>
                                ) : detail.type === "email" ? (
                                  <a
                                    key={detailIndex}
                                    href={detail.href}
                                    className="text-primary hover:text-accent transition-colors text-sm underline block break-words"
                                  >
                                    {detail.text}
                                  </a>
                                ) : detail.type === "phone" ? (
                                  <a
                                    key={detailIndex}
                                    href={detail.href}
                                    className="text-primary hover:text-accent transition-colors text-sm underline block"
                                  >
                                    {detail.text}
                                  </a>
                                ) : detail.type === "address" ? (
                                  <p key={detailIndex} className="text-muted-foreground text-sm break-words">
                                    {detail.text}
                                  </p>
                                ) : (
                                  <p key={detailIndex} className="text-muted-foreground text-sm">
                                    {detail.text}
                                  </p>
                                )
                              ))}
                            </div>
                          </div>
                        </Card>
                      </motion.div>
                    );
                  })}
                </div>
              </Card>
            </motion.div>
          </div>

          {/* Second Row - Google Maps (Full Width) */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="w-full"
          >
            <GoogleMap />
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-15 bg-gradient-to-b from-background/50 to-[#111111]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">'
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">Frequently Asked Questions</h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto text-justify">
              Quick answers to common questions about our AI solutions and services
            </p>
          </motion.div>

          <div className="max-w-4xl mx-auto space-y-6">
            {[
              {
                question: "How quickly can we see results from your AI solutions?",
                answer: "Most clients see initial results within 4-6 weeks of implementation, with full ROI typically achieved within 6-12 months."
              },
              {
                question: "Do you provide ongoing support and maintenance?",
                answer: "Yes, we offer 24/7 support and continuous model optimization to ensure peak performance of your AI systems."
              },
              {
                question: "Can your solutions integrate with our existing systems?",
                answer: "Absolutely. Our solutions are designed to seamlessly integrate with most enterprise systems through APIs and standard protocols."
              },
              {
                question: "What industries do you serve?",
                answer: "We serve manufacturing, healthcare, biotechnology, automotive, energy, and many other industries requiring advanced AI solutions."
              }
            ].map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="glass p-6 hover:border-primary/50 transition-colors">
                  <h3 className="font-bold mb-3 text-lg">{faq.question}</h3>
                  <p className="text-muted-foreground leading-relaxed text-justify">{faq.answer}</p>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}